import React from 'react'
import { Icon } from './icons'
import { fmtRelTime } from './format'
import { Avatar, DataState, Dropdown, MCAvatar, Modal } from './shared'
import type { ApiError, AuditRow, LifecycleAction, PlayerRecord, Role, Server, ToastPush, User } from './types'

interface LifecycleModalProps {
  action?: LifecycleAction
  server?: Server
  onConfirm: (options: { announce: boolean }) => void
  onClose: () => void
}

export function LifecycleModal({ action, server, onConfirm, onClose }: LifecycleModalProps) {
  const [announce, setAnnounce] = React.useState(true)
  const [secondConfirm, setSecondConfirm] = React.useState(false)
  const [count, setCount] = React.useState(30)
  const [running, setRunning] = React.useState(false)

  React.useEffect(() => {
    setAnnounce(true)
    setSecondConfirm(false)
    setCount(30)
    setRunning(false)
  }, [action, server?.id])

  React.useEffect(() => {
    if (!running) return
    if (count <= 0) {
      onConfirm({ announce })
      return
    }
    const timerId = window.setTimeout(() => setCount(current => current - 1), 1000)
    return () => window.clearTimeout(timerId)
  }, [announce, count, onConfirm, running])

  if (!action || !server) return null

  const title = action === 'start' ? `Start ${server.name}?` :
    action === 'stop' ? `Stop ${server.name}?` :
      `Restart ${server.name}?`

  const playerCount = server.players.length
  const needsSecondConfirm = !announce && playerCount > 0 && action !== 'start'

  if (action === 'start') {
    return (
      <Modal open onClose={onClose}>
        <h2>{title}</h2>
        <p>Low ceremony - confirm once and the unit will start.</p>
        <div className="actions">
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => onConfirm({ announce: false })}>
            <Icon name="play" size={12} /> Start
          </button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal open onClose={onClose}>
      <h2>{title}</h2>
      <p>{action === 'stop' ? 'The server will shut down.' : 'The server will stop, then start again.'}</p>

      {playerCount > 0 && (
        <div className="banner warn" style={{ marginBottom: 12 }}>
          <Icon name="alert-triangle" size={14} style={{ marginTop: 1 }} />
          <div>
            <strong>{playerCount} player{playerCount > 1 ? 's' : ''} currently online.</strong> They will be disconnected.
          </div>
        </div>
      )}

      <label className="check mb-2">
        <input type="checkbox" checked={announce} onChange={event => setAnnounce(event.target.checked)} />
        Announce to server first (say at 30s / 10s / 5s)
      </label>

      {needsSecondConfirm && (
        <label className="check mb-2" style={{ color: 'var(--rose-400)' }}>
          <input type="checkbox" checked={secondConfirm} onChange={event => setSecondConfirm(event.target.checked)} />
          I understand players will be disconnected without warning.
        </label>
      )}

      {running && (
        <>
          <div className="text-sm mono" style={{ color: 'var(--amber-400)' }}>
            Executing in {count}s - click cancel to abort.
          </div>
          <div className="countdown-bar"><div className="fill" style={{ width: `${(count / 30) * 100}%` }} /></div>
        </>
      )}

      <div className="actions">
        <button className="btn ghost" onClick={onClose}>{running ? 'Cancel' : 'Close'}</button>
        {!running ? (
          <button
            className={`btn ${action === 'stop' ? 'danger' : 'primary'}`}
            disabled={needsSecondConfirm && !secondConfirm}
            onClick={() => setRunning(true)}
          >
            {action === 'stop' ? 'Stop' : 'Restart'} in 30s
          </button>
        ) : (
          <button className="btn ghost" onClick={onClose}>Cancel ({count}s)</button>
        )}
      </div>

      <div className="text-xs dim mt-3">
        Audit rows: <span className="mono">lifecycle.{action}.requested</span> now and <span className="mono">lifecycle.{action}.completed</span> on finish.
      </div>
    </Modal>
  )
}

interface PlayersPageProps {
  role: Role
  players: PlayerRecord[]
  loading: boolean
  error: ApiError | null
  onRetry: () => void
  onHeal: (playerName: string) => void
  toast: ToastPush
}

export function PlayersPage({ role, players, loading, error, onRetry, onHeal, toast }: PlayersPageProps) {
  const [query, setQuery] = React.useState('')

  const filtered = React.useMemo(() => {
    const search = query.trim().toLowerCase()
    if (!search) return players
    return players.filter(row =>
      (row.user.mcUsername ?? '').toLowerCase().includes(search) ||
      row.user.displayName.toLowerCase().includes(search) ||
      row.user.username.toLowerCase().includes(search),
    )
  }, [players, query])

  return (
    <>
      <div className="page-header" data-screen-label="Players">
        <h1>Players</h1>
        <div className="header-right">
          <div style={{ position: 'relative' }}>
            <Icon name="search" size={13} style={{ position: 'absolute', left: 10, top: 8, color: 'var(--text-dim)' }} />
            <input placeholder="Search players..." value={query} onChange={event => setQuery(event.target.value)} style={{ paddingLeft: 30, width: 220 }} />
          </div>
        </div>
      </div>

      <DataState loading={loading} error={error} empty={!loading && !error && players.length === 0} emptyLabel="No players yet." onRetry={onRetry}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-scroll">
            <table className="data-table reflow">
              <thead>
                <tr>
                  <th>MC Player</th>
                  <th>Display name</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.user.id}>
                    <td data-label="MC Player">
                      <div className="flex items-center gap-2">
                        <MCAvatar name={row.user.mcUsername ?? '?'} />
                        <span className="mono">{row.user.mcUsername ?? '—'}</span>
                      </div>
                    </td>
                    <td data-label="Display name">
                      <div className="flex items-center gap-2">
                        <Avatar user={row.user} />
                        <span>{row.user.displayName}</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ textAlign: 'center', padding: 24 }} className="dim">No players matched your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DataState>
    </>
  )
}

interface AuditPageProps {
  role: Role
  rows: AuditRow[]
  users: User[]
  loading: boolean
  error: ApiError | null
  onRetry: () => void
}

export function AuditPage({ role, rows, users, loading, error, onRetry }: AuditPageProps) {
  const [actionFilter, setActionFilter] = React.useState('all')
  const [outcomeFilter, setOutcomeFilter] = React.useState('all')
  const [serverFilter, setServerFilter] = React.useState('all')
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null)

  const serverValues = React.useMemo(() => {
    const set = new Set<string>()
    rows.forEach(row => {
      if (row.server) set.add(row.server)
    })
    return Array.from(set)
  }, [rows])

  let filtered = rows
  if (actionFilter !== 'all') filtered = filtered.filter(row => row.action.startsWith(actionFilter))
  if (outcomeFilter !== 'all') filtered = filtered.filter(row => row.outcome === outcomeFilter)
  if (serverFilter !== 'all') filtered = filtered.filter(row => row.server === serverFilter)

  return (
    <>
      <div className="page-header" data-screen-label="Audit Log">
        <h1>Audit Log</h1>
        <span className="subtitle">- every privileged action - {role === 'player' ? 'your rows only' : 'all rows'}</span>
      </div>

      <div className="filter-bar">
        <Icon name="filter" size={13} style={{ color: 'var(--text-dim)', marginRight: 2 }} />
        <Dropdown
          size="sm"
          value={serverFilter}
          onChange={setServerFilter}
          options={[
            { value: 'all', label: 'All servers' },
            ...serverValues.map(v => ({ value: v, label: v })),
          ]}
        />
        <Dropdown
          size="sm"
          value={actionFilter}
          onChange={setActionFilter}
          options={[
            { value: 'all', label: 'All actions' },
            { value: 'lifecycle', label: 'lifecycle.*' },
            { value: 'rcon', label: 'rcon.*' },
            { value: 'plugin', label: 'plugin.*' },
            { value: 'settings', label: 'settings.*' },
            { value: 'auth', label: 'auth.*' },
            { value: 'user', label: 'user.*' },
          ]}
        />
        <Dropdown
          size="sm"
          value={outcomeFilter}
          onChange={setOutcomeFilter}
          options={[
            { value: 'all', label: 'All outcomes' },
            { value: 'ok', label: 'ok' },
            { value: 'denied', label: 'denied' },
            { value: 'rejected', label: 'rejected' },
            { value: 'error', label: 'error' },
          ]}
        />
        <div className="ml-auto dim text-xs">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</div>
      </div>

      <DataState loading={loading} error={error} empty={!loading && !error && rows.length === 0} emptyLabel="No audit rows yet." onRetry={onRetry}>
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-scroll">
            <table className="data-table reflow">
              <thead>
                <tr>
                  <th>Time</th><th>User</th><th>Server</th><th>Action</th><th>Outcome</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, index) => {
                  const user = users.find(item => item.username === row.user)
                  const expanded = expandedIdx === index
                  return (
                    <React.Fragment key={row.id ?? `${row.ts}-${index}`}>
                      <tr onClick={() => setExpandedIdx(expanded ? null : index)} style={{ cursor: 'pointer' }}>
                        <td data-label="Time">
                          <div className="mono text-xs">{row.ts.replace('T', ' ').replace('Z', '')}</div>
                          <div className="dim text-xs">{fmtRelTime(row.ts)}</div>
                        </td>
                        <td data-label="User">
                          <div className="flex items-center gap-2">
                            <Avatar user={user} />
                            <div>
                              <div>{user?.displayName || row.user}</div>
                              <div className="dim text-xs">{row.role}</div>
                            </div>
                          </div>
                        </td>
                        <td data-label="Server"><span className="mono text-xs">{row.server || '—'}</span></td>
                        <td data-label="Action"><span className="mono text-xs">{row.action}</span></td>
                        <td data-label="Outcome">
                          {row.outcome === 'ok' && <span className="chip ok">ok</span>}
                          {row.outcome === 'denied' && <span className="chip warn">denied</span>}
                          {row.outcome === 'rejected' && <span className="chip err">rejected</span>}
                          {row.outcome === 'error' && <span className="chip err">error</span>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={13} style={{ color: 'var(--text-dim)' }} />
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} style={{ background: 'var(--bg)', padding: 16 }}>
                            <div className="text-xs dim mb-1">payload</div>
                            <pre className="mono text-xs" style={{ margin: 0, color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
{JSON.stringify(row.payload, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: 24 }} className="dim">No audit rows matched the current filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DataState>
    </>
  )
}

interface CreateUserBody {
  username: string
  displayName: string
  mcUsername?: string
  role: 'owner' | 'admin' | 'player'
  accentColor: string
  home?: string
  password: string
}

interface UpdateUserBody {
  displayName?: string
  mcUsername?: string
  role?: 'owner' | 'admin' | 'player'
  accentColor?: string
  home?: string
  password?: string
}

interface SettingsPageProps {
  users: User[]
  loading: boolean
  error: ApiError | null
  onRetry: () => void
  onSaveMapping: (userId: string, mcName: string) => void
  onRevokeAllSessions: () => void
  onCreateUser: (body: CreateUserBody) => Promise<void>
  onUpdateUser: (id: string, body: UpdateUserBody) => Promise<void>
  onDeleteUser: (id: string) => Promise<void>
  toast: ToastPush
}

export function SettingsPage({ users, loading, error, onRetry, onSaveMapping, onRevokeAllSessions, onCreateUser, onUpdateUser, onDeleteUser, toast }: SettingsPageProps) {
  const [drafts, setDrafts] = React.useState<Record<string, string>>({})
  const [showCreate, setShowCreate] = React.useState(false)
  const [createBusy, setCreateBusy] = React.useState(false)
  const [createForm, setCreateForm] = React.useState<CreateUserBody>({
    username: '',
    displayName: '',
    mcUsername: '',
    role: 'player',
    accentColor: '#6366f1',
    password: '',
  })

  React.useEffect(() => {
    setDrafts(Object.fromEntries(users.map(user => [user.id, user.mcUsername ?? ''])))
  }, [users])

  async function handleCreate() {
    if (!createForm.username || !createForm.displayName || createForm.password.length < 8) {
      toast({ variant: 'warn', title: 'Please fill all fields', sub: 'Password must be 8+ chars.' })
      return
    }
    setCreateBusy(true)
    try {
      await onCreateUser({
        ...createForm,
        mcUsername: createForm.mcUsername || undefined,
        home: createForm.home || undefined,
      })
      toast({ variant: 'success', title: 'User created', sub: createForm.username })
      setCreateForm({ username: '', displayName: '', mcUsername: '', role: 'player', accentColor: '#6366f1', password: '' })
      setShowCreate(false)
    } catch (exc) {
      toast({ variant: 'error', title: 'Could not create user', sub: (exc as Error).message })
    } finally {
      setCreateBusy(false)
    }
  }

  async function handleRoleChange(user: User, role: 'owner' | 'admin' | 'player') {
    try {
      await onUpdateUser(user.id, { role })
      toast({ variant: 'success', title: 'Role updated', sub: `${user.username} → ${role}` })
    } catch (exc) {
      toast({ variant: 'error', title: 'Could not update role', sub: (exc as Error).message })
    }
  }

  async function handleResetPassword(user: User) {
    const next = window.prompt(`New password for ${user.username} (8+ chars):`)
    if (!next) return
    if (next.length < 8) {
      toast({ variant: 'warn', title: 'Password too short' })
      return
    }
    try {
      await onUpdateUser(user.id, { password: next })
      toast({ variant: 'success', title: 'Password reset', sub: user.username })
    } catch (exc) {
      toast({ variant: 'error', title: 'Could not reset password', sub: (exc as Error).message })
    }
  }

  async function handleDelete(user: User) {
    if (!window.confirm(`Delete user ${user.username}? They will be signed out.`)) return
    try {
      await onDeleteUser(user.id)
      toast({ variant: 'info', title: 'User deleted', sub: user.username })
    } catch (exc) {
      toast({ variant: 'error', title: 'Could not delete user', sub: (exc as Error).message })
    }
  }

  return (
    <>
      <div className="page-header" data-screen-label="Settings">
        <h1>Settings</h1>
        <span className="subtitle">- owner only - panel configuration</span>
      </div>

      <DataState loading={loading} error={error} onRetry={onRetry}>
        <div className="flex-col gap-4">
          <div className="card">
            <div className="flex items-center gap-2 mb-3">
              <h3 style={{ margin: 0 }}>Users</h3>
              <span className="chip">{users.length}</span>
              <button className="btn sm primary ml-auto" onClick={() => setShowCreate(!showCreate)}>
                <Icon name="plus" size={11} /> {showCreate ? 'Cancel' : 'New user'}
              </button>
            </div>

            {showCreate && (
              <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 12 }}>
                <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input placeholder="username (lowercase)" value={createForm.username} onChange={e => setCreateForm({ ...createForm, username: e.target.value.toLowerCase() })} />
                  <input placeholder="Display name" value={createForm.displayName} onChange={e => setCreateForm({ ...createForm, displayName: e.target.value })} />
                  <input placeholder="MC username (optional)" value={createForm.mcUsername} onChange={e => setCreateForm({ ...createForm, mcUsername: e.target.value })} />
                  <Dropdown
                    value={createForm.role}
                    onChange={v => setCreateForm({ ...createForm, role: v as CreateUserBody['role'] })}
                    options={[
                      { value: 'player', label: 'player' },
                      { value: 'admin', label: 'admin' },
                      { value: 'owner', label: 'owner' },
                    ]}
                  />
                  <input placeholder="home coords (optional, e.g. 128 74 -192)" value={createForm.home ?? ''} onChange={e => setCreateForm({ ...createForm, home: e.target.value })} />
                  <input type="password" placeholder="password (8+ chars)" value={createForm.password} onChange={e => setCreateForm({ ...createForm, password: e.target.value })} />
                </div>
                <div className="flex gap-2 mt-3 justify-end">
                  <button className="btn ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                  <button className="btn primary" disabled={createBusy} onClick={() => void handleCreate()}>Create user</button>
                </div>
              </div>
            )}

            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>User</th><th>Role</th><th>MC username</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <Avatar user={user} />
                          <div>
                            <div>{user.displayName}</div>
                            <div className="dim text-xs mono">{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <Dropdown
                          value={user.role}
                          onChange={v => void handleRoleChange(user, v as 'owner' | 'admin' | 'player')}
                          options={[
                            { value: 'player', label: 'player' },
                            { value: 'admin', label: 'admin' },
                            { value: 'owner', label: 'owner' },
                          ]}
                        />
                      </td>
                      <td>
                        <div className="flex gap-2 items-center">
                          <input value={drafts[user.id] ?? ''} onChange={event => setDrafts(current => ({ ...current, [user.id]: event.target.value }))} style={{ width: 160 }} className="mono" />
                          <button className="btn sm ghost" onClick={() => onSaveMapping(user.id, drafts[user.id] ?? '')}>Save</button>
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="flex gap-2 justify-end">
                          <button className="btn sm ghost" onClick={() => void handleResetPassword(user)}>Reset pw</button>
                          <button className="btn sm ghost" style={{ color: 'var(--rose-400)' }} onClick={() => void handleDelete(user)}>
                            <Icon name="trash" size={11} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ borderColor: 'color-mix(in oklab, var(--err) 30%, var(--border))' }}>
            <h3 className="mb-3" style={{ color: 'var(--rose-400)' }}>Danger zone</h3>
            <div className="flex-col gap-2">
              <button className="btn ghost" style={{ justifyContent: 'flex-start' }} onClick={onRevokeAllSessions}>Revoke all sessions</button>
              <button className="btn ghost" style={{ justifyContent: 'flex-start', color: 'var(--rose-400)' }} onClick={() => toast({ variant: 'warn', title: 'Not implemented', sub: 'Audit log wipe remains Phase 2.' })}>Wipe audit log...</button>
            </div>
            <div className="text-xs dim mt-3">Type-to-confirm remains a Phase 2 follow-up.</div>
          </div>
        </div>
      </DataState>
    </>
  )
}

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<void>
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!username.trim() || !password) return
    setBusy(true)
    setError(null)
    try {
      await onLogin(username.trim().toLowerCase(), password)
    } catch (exc) {
      const status = (exc as { status?: number }).status ?? 0
      if (status === 401) setError('Invalid username or password.')
      else if (status === 429) setError('Too many attempts — wait a minute.')
      else setError((exc as Error).message || 'Sign in failed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="big-logo">M</div>
        <h1>mcpanel</h1>
        <div className="tag">Admin panel for your Minecraft servers</div>
        <input
          className="login-input"
          placeholder="Username"
          value={username}
          onChange={event => setUsername(event.target.value)}
          autoComplete="username"
          autoFocus
          disabled={busy}
        />
        <input
          className="login-input"
          type="password"
          placeholder="Password"
          value={password}
          onChange={event => setPassword(event.target.value)}
          autoComplete="current-password"
          disabled={busy}
        />
        {error && <div className="login-error">{error}</div>}
        <button className="btn primary login-btn" type="submit" disabled={busy || !username.trim() || !password}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className="access-note">Accounts are created by the panel owner via the Users tab or the CLI.</div>
      </form>
    </div>
  )
}

export function NotAuthorizedPage({ onSignOut }: { onSignOut: () => void }) {
  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="big-logo" style={{ background: 'color-mix(in oklab, var(--err) 30%, var(--surface-2))' }}>
          <Icon name="alert-circle" size={26} />
        </div>
        <h1>Not authorized.</h1>
        <div className="tag" style={{ maxWidth: 320, margin: '6px auto 26px' }}>
          Your account cannot access this panel. Ask the owner for a role upgrade.
        </div>
        <button className="btn ghost" onClick={onSignOut} style={{ width: '100%', padding: 10 }}>Sign out</button>
      </div>
    </div>
  )
}
